import { useState, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import API from '../services/api';
import usePermissionSync from './usePermissionSync';

const usePermissionCheck = (requiredPermission) => {
  const [hasPermission, setHasPermission] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userAccountType, setUserAccountType] = useState(null);
  const [userPermissions, setUserPermissions] = useState({});

  // 权限同步hook（仅对RAM账号有效）
  const { permissions: syncedPermissions } = usePermissionSync(userAccountType);

  const checkPermission = useCallback(async (forcedPermissions = null) => {
    setIsLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setHasPermission(false);
        setIsLoading(false);
        return;
      }

      // 首先尝试从JWT中获取权限信息
      let permissions = forcedPermissions || {};
      let accountType = null;
      
      try {
        const decodedToken = jwtDecode(token);
        accountType = decodedToken.accountType;
        if (!forcedPermissions) {
          permissions = decodedToken.ramPermissions || decodedToken.permissions || {};
        }
      } catch (jwtError) {
        console.warn('JWT解析失败，尝试从API获取用户信息:', jwtError);
      }

      // 如果JWT中没有权限信息或账号类型，通过API获取
      if (!accountType || (!forcedPermissions && Object.keys(permissions).length === 0)) {
        try {
          const response = await API.get('/api/user/profile');
          accountType = response.data.accountType;
          if (!forcedPermissions) {
            permissions = response.data.permissions || response.data.ramPermissions || {};
          }
        } catch (apiError) {
          console.error('获取用户信息失败:', apiError);
          setHasPermission(false);
          setIsLoading(false);
          return;
        }
      }

      setUserAccountType(accountType);
      setUserPermissions(permissions);

      // 如果传递了具体权限参数，进行权限检查
      if (requiredPermission) {
        // 主账号拥有所有权限
        if (accountType === 'main') {
          setHasPermission(true);
        } else if (accountType === 'ram') {
          // RAM账号需要检查具体权限
          setHasPermission(permissions[requiredPermission] === true);
        } else {
          // 其他账号类型默认无权限
          setHasPermission(false);
        }
      } else {
        // 没有传递权限参数时，不进行权限检查，只获取账号信息
        setHasPermission(true);
      }
    } catch (error) {
      console.error('权限检查失败:', error);
      setHasPermission(false);
    } finally {
      setIsLoading(false);
    }
  }, [requiredPermission]); // 只依赖requiredPermission

  useEffect(() => {
    checkPermission();
  }, [requiredPermission, checkPermission]);

  // 监听权限更新事件（仅对RAM账号）
  useEffect(() => {
    if (userAccountType === 'ram' && Object.keys(syncedPermissions).length > 0) {
      // 静默检查权限更新
      checkPermission(syncedPermissions);
    }
  }, [syncedPermissions, userAccountType, checkPermission]);

  // 监听全局权限更新事件
  useEffect(() => {
    const handlePermissionsUpdated = (event) => {
      const { permissions } = event.detail;
      // 静默处理权限更新
      checkPermission(permissions);
    };

    window.addEventListener('permissionsUpdated', handlePermissionsUpdated);
    
    return () => {
      window.removeEventListener('permissionsUpdated', handlePermissionsUpdated);
    };
  }, [checkPermission]);

  return {
    hasPermission,
    isLoading,
    userAccountType,
    userPermissions,
    refreshPermissions: () => checkPermission() // 提供手动刷新方法
  };
};

export default usePermissionCheck; 